/**
 * SalesManagerRow Component
 *
 * Expandable row displaying a single sales manager's performance.
 * Collapsed: Shows rank, name, total gross, and units breakdown.
 * Expanded: Shows front gross, holdback, F&I gross with per-unit averages.
 */

import React from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { SalesManager } from './types';

interface SalesManagerRowProps {
  manager: SalesManager;
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format per-unit currency
 */
function formatPerUnit(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Drill-down rows for the expanded view
 */
const DRILL_DOWN_ROWS: Array<{
  label: string;
  grossKey: keyof SalesManager;
  avgKey: keyof SalesManager;
}> = [
  { label: 'Front Gross', grossKey: 'frontGross', avgKey: 'avgFrontGrossPerUnit' },
  { label: 'Holdback', grossKey: 'holdback', avgKey: 'avgHoldbackPerUnit' },
  { label: 'F&I Gross', grossKey: 'fiGross', avgKey: 'avgFiGrossPerUnit' },
];

export const SalesManagerRow: React.FC<SalesManagerRowProps> = ({
  manager,
  isExpanded,
  onToggle,
  rank,
}) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Main row - always visible */}
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <span className="font-medium text-gray-900 truncate">
            {manager.managerName}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-blue-600">
              {formatCurrency(manager.totalGross)}
            </span>
          </div>
          <div className="text-right text-sm w-52">
            <span className="text-blue-700 font-medium">{manager.newUnits}</span>
            <span className="text-gray-400"> new</span>
            {(manager.swapUnits ?? 0) > 0 && (
              <>
                <span className="text-blue-400"> +{manager.swapUnits}</span>
                <span className="text-gray-400"> swp</span>
              </>
            )}
            <span className="text-gray-400"> / </span>
            <span className="text-gray-700 font-medium">{manager.usedUnits}</span>
            <span className="text-gray-400"> used</span>
            {(manager.wholesaleUnits ?? 0) > 0 && (
              <>
                <span className="text-purple-500"> +{manager.wholesaleUnits}</span>
                <span className="text-gray-400"> whl</span>
              </>
            )}
            <span className="text-gray-400"> = </span>
            <span className="text-gray-900 font-semibold">{manager.totalUnits}</span>
          </div>
        </div>
      </div>

      {/* Expanded details - gross breakdown */}
      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Metric</th>
                <th className="text-right py-2 px-3 font-medium">Total</th>
                <th className="text-right py-2 px-3 font-medium">Per Unit</th>
              </tr>
            </thead>
            <tbody>
              {DRILL_DOWN_ROWS.map((row) => (
                <tr
                  key={row.label}
                  className="border-t border-gray-200 hover:bg-white"
                >
                  <td className="py-2 px-3 text-gray-700">{row.label}</td>
                  <td className="py-2 px-3 text-right text-gray-900 font-medium">
                    {formatCurrency(manager[row.grossKey] as number)}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600">
                    {formatPerUnit(manager[row.avgKey] as number | null)}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
                <td className="py-2 px-3 text-gray-700">Total</td>
                <td className="py-2 px-3 text-right text-blue-600 font-bold">
                  {formatCurrency(manager.totalGross)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatPerUnit(manager.avgTotalGrossPerUnit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
